import { useEffect, useState } from "react";
import {
  Drawer,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Typography,
  Table,
  Space,
  Divider,
  Empty,
  Radio,
  Popconfirm,
  Tag,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  ArrowRightOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { loadAllProductSubCategory } from "../../redux/actions/productSubCategory/getProductSubCategory";
import data from "../../data/rawMaterialData";
import LayerSelectorBasic from "../Card/LayerSelectorBasic";
import LayerSelectorAdvance from "../Card/LayerSelectorAdvance";
import LayerTemplateSelector from "../Card/LayerTemplateSelector";
import PrintSelector from "../Card/PrintSelector";
import CoatingSelector from "../Card/CoatingSelector";
import { buildLayersForPly } from "../../utils/layerUtils";

const { Title, Text } = Typography;

const layerObj = {
  layerId: 0,
  layerType: "",
  materialType: "",
  gsm: "",
  bf: "",
  ff: "",
};

const OrderBulkItemsModal = ({
  visible,
  onCancel,
  onSave,
  pricingType = "individual",
}) => {
  const dispatch = useDispatch();
  const subCategory = useSelector(
    (state) => state.productSubCategories?.list || []
  );
  const { unitType, ply: plyOptions, castingType } = data;
  const [form] = Form.useForm();

  const [currentStep, setCurrentStep] = useState(1);
  const [rows, setRows] = useState([]);

  const [commonFields, setCommonFields] = useState({
    subcategory: null,
    length: null,
    width: null,
    height: null,
    unitType: null,
    castingType: null,
    quantity: null,
    ratePerUnit: null,
    ply: null,
    isPrinting: false,
    isCoating: false,
    layerSelector: false,
    topConversionRate: null,
    boxConversionRate: null,
    rs: null,
    cs: null,
    boxesPerSheet: null,
    sheetsPerBox: null,
    remarks: null,
    boxType: null,
    rateType: "individual",
    packingQuantity: null,
    joiningMethod: null,
    packingRole: null,
    qtyPerPack: null,
  });

  const [layers, setLayers] = useState([]);
  const [gsm, setGsm] = useState([]);
  const [bf, setBf] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printing, setPrinting] = useState({});
  const [isCoating, setIsCoating] = useState(false);
  const [coating, setCoating] = useState({ coatings: [] });
  const [layerSelector, setLayerSelector] = useState(false);

  const isCombined =
    pricingType === "inner_combined" || pricingType === "outer_combined";

  useEffect(() => {
    if (visible) {
      dispatch(loadAllProductSubCategory({ page: 1, limit: 100 }));
    }
  }, [dispatch, visible]);

  useEffect(() => {
    if (!visible) {
      resetForm();
    }
  }, [visible]);

  const resetForm = () => {
    form.resetFields();
    setCurrentStep(1);
    setRows([]);
    setCommonFields({
      subcategory: null,
      length: null,
      width: null,
      height: null,
      unitType: null,
      castingType: null,
      quantity: null,
      ratePerUnit: null,
      ply: null,
      isPrinting: false,
      isCoating: false,
      layerSelector: false,
      topConversionRate: null,
      boxConversionRate: null,
      rs: null,
      cs: null,
      boxesPerSheet: null,
      sheetsPerBox: null,
      remarks: null,
      boxType: null,
      rateType: "individual",
      packingQuantity: null,
      joiningMethod: null,
      packingRole: null,
      qtyPerPack: null,
    });
    setLayers([]);
    setGsm([]);
    setBf([]);
    setIsPrinting(false);
    setPrinting({});
    setIsCoating(false);
    setCoating({ coatings: [] });
    setLayerSelector(false);
  };

  const handlePlyChange = (plyVal) => {
    setCommonFields((prev) => ({ ...prev, ply: plyVal }));
    const { layersArray, gsmArray, bfArray } = buildLayersForPly(
      plyVal,
      layers,
      layerObj
    );
    setLayers(layersArray);
    setGsm(gsmArray);
    setBf(bfArray);
  };

  const handleSelectorChange = (e) => {
    const isAdvance = e.target.value === "advance";
    setLayerSelector(isAdvance);
    setCommonFields((prev) => ({ ...prev, layerSelector: isAdvance }));
  };

  const handlePrintingChange = (e) => {
    const enabled = e.target.value === "yes";
    setIsPrinting(enabled);
    if (!enabled) setPrinting({});
    setCommonFields((prev) => ({ ...prev, isPrinting: enabled }));
  };

  const handleCoatingChange = (e) => {
    const enabled = e.target.value === "yes";
    setIsCoating(enabled);
    if (!enabled) setCoating({ coatings: [] });
    setCommonFields((prev) => ({ ...prev, isCoating: enabled }));
  };

  const handleProceedToStep2 = async () => {
    try {
      const values = await form.validateFields(["subcategory"]);
      setCommonFields((prev) => ({ ...prev, subcategory: values.subcategory }));
      setCurrentStep(2);
    } catch (error) {
      toast.error("Please fill required fields");
    }
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        key: Date.now() + prev.length,
        name: "",
        length: commonFields.length,
        width: commonFields.width,
        height: commonFields.height,
        unitType: commonFields.unitType,
        castingType: commonFields.castingType,
        quantity: commonFields.quantity,
        ratePerUnit: commonFields.ratePerUnit,
        ply: commonFields.ply,
        packingRole: commonFields.packingRole,
        qtyPerPack: commonFields.qtyPerPack,
      },
    ]);
  };

  const updateRow = (key, field, value) => {
    setRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, [field]: value } : row))
    );
  };

  const deleteRow = (key) => {
    setRows((prev) => prev.filter((row) => row.key !== key));
  };

  const buildItemFromRow = (row) => {
    const qtyPerPack = isCombined
      ? Number(row.qtyPerPack || commonFields.qtyPerPack || 1)
      : null;
    const packingRole = isCombined
      ? row.packingRole || commonFields.packingRole || "inner"
      : null;
    const quantity = isCombined
      ? packingRole === "outer"
        ? 1
        : qtyPerPack
      : Number(row.quantity);
    const ratePerUnit = pricingType === "individual"
      ? Number(row.ratePerUnit || 0)
      : 0;

    return {
      item_name: row.name,
      item_description: "",
      subcategory: commonFields.subcategory,
      number_of_pieces: 1,
      length: Number(row.length),
      width: Number(row.width),
      height: Number(row.height),
      unit_type: row.unitType,
      casting_type: row.castingType,
      ply: Number(row.ply),
      layers,
      is_printing: isPrinting,
      printing_config: printing,
      is_coating: isCoating,
      coating_config: coating,
      joining_method: commonFields.joiningMethod || null,
      top_conversion_rate: Number(commonFields.topConversionRate || 0),
      box_conversion_rate: Number(commonFields.boxConversionRate || 0),
      rs: commonFields.rs ? Number(commonFields.rs) : null,
      cs: commonFields.cs ? Number(commonFields.cs) : null,
      boxes_per_sheet: commonFields.boxesPerSheet
        ? Number(commonFields.boxesPerSheet)
        : null,
      sheets_per_box: commonFields.sheetsPerBox
        ? Number(commonFields.sheetsPerBox)
        : null,
      box_type: commonFields.boxType || null,
      rate_type: commonFields.rateType || null,
      packing_quantity_rs: commonFields.packingQuantity
        ? Number(commonFields.packingQuantity)
        : null,
      item_remarks: commonFields.remarks || null,
      quantity,
      rate_per_unit: ratePerUnit,
      total_amount:
        pricingType === "individual" ? quantity * ratePerUnit : 0,
      packing_role: packingRole,
      qty_per_pack: qtyPerPack,
      calculated_rate: 0,
      markup_percentage: 0,
      discount_percentage: 0,
      roll_size: null,
      cutting_size: null,
      net_gsm: null,
      top_gsm: null,
      box_weight: null,
      material_cost_top: 0,
      material_cost_rest: 0,
      print_cost: 0,
      coating_cost_total: 0,
      coating_cost_lamination: 0,
      coating_cost_leafing: 0,
      coating_cost_spot_uv: 0,
      fixed_cost_total: 0,
      fixed_cost_per_box: 0,
      selected_machines: null,
      outsource_jobs: null,
    };
  };

  const handleBulkSave = () => {
    if (rows.length === 0) {
      toast.error("Please add at least one item row");
      return;
    }

    const invalidRows = rows.filter((row) => {
      if (!row.name || !row.length || !row.width || !row.height || !row.unitType || !row.castingType || !row.ply) {
        return true;
      }
      if (isCombined) {
        return !((row.packingRole || commonFields.packingRole) && (row.qtyPerPack || commonFields.qtyPerPack || (row.packingRole || commonFields.packingRole) === "outer"));
      }
      return !row.quantity;
    });

    if (invalidRows.length > 0) {
      toast.error("Please fill all required row fields before saving");
      return;
    }

    onSave(rows.map(buildItemFromRow));
    resetForm();
  };

  const tableColumns = [
    {
      title: "Item Name",
      dataIndex: "name",
      key: "name",
      width: 180,
      render: (text, record) => (
        <Input
          value={text}
          onChange={(e) => updateRow(record.key, "name", e.target.value)}
          placeholder="e.g. Inner Box"
        />
      ),
    },
    {
      title: "Length",
      dataIndex: "length",
      key: "length",
      width: 100,
      render: (text, record) =>
        commonFields.length ? (
          <span>{commonFields.length}</span>
        ) : (
          <InputNumber
            value={text}
            onChange={(val) => updateRow(record.key, "length", val)}
            style={{ width: "100%" }}
          />
        ),
    },
    {
      title: "Width",
      dataIndex: "width",
      key: "width",
      width: 100,
      render: (text, record) =>
        commonFields.width ? (
          <span>{commonFields.width}</span>
        ) : (
          <InputNumber
            value={text}
            onChange={(val) => updateRow(record.key, "width", val)}
            style={{ width: "100%" }}
          />
        ),
    },
    {
      title: "Height",
      dataIndex: "height",
      key: "height",
      width: 100,
      render: (text, record) =>
        commonFields.height ? (
          <span>{commonFields.height}</span>
        ) : (
          <InputNumber
            value={text}
            onChange={(val) => updateRow(record.key, "height", val)}
            style={{ width: "100%" }}
          />
        ),
    },
    {
      title: "Unit",
      dataIndex: "unitType",
      key: "unitType",
      width: 110,
      render: (text, record) =>
        commonFields.unitType ? (
          <span>{commonFields.unitType}</span>
        ) : (
          <Select
            value={text}
            onChange={(val) => updateRow(record.key, "unitType", val)}
            style={{ width: "100%" }}
            options={unitType?.map((u) => ({ label: u, value: u })) || []}
          />
        ),
    },
    {
      title: "Casting",
      dataIndex: "castingType",
      key: "castingType",
      width: 130,
      render: (text, record) =>
        commonFields.castingType ? (
          <span>{commonFields.castingType}</span>
        ) : (
          <Select
            value={text}
            onChange={(val) => updateRow(record.key, "castingType", val)}
            style={{ width: "100%" }}
            options={
              castingType?.map((c) => ({ label: c.name, value: c.value })) || []
            }
          />
        ),
    },
    ...(isCombined
      ? [
          {
            title: "Packing Role",
            dataIndex: "packingRole",
            key: "packingRole",
            width: 130,
            render: (text, record) =>
              commonFields.packingRole ? (
                <span>{commonFields.packingRole}</span>
              ) : (
                <Select
                  value={text}
                  onChange={(val) => updateRow(record.key, "packingRole", val)}
                  style={{ width: "100%" }}
                  options={[
                    { label: "Inner", value: "inner" },
                    { label: "Outer", value: "outer" },
                  ]}
                />
              ),
          },
          {
            title: "Qty / Pack",
            dataIndex: "qtyPerPack",
            key: "qtyPerPack",
            width: 110,
            render: (text, record) =>
              commonFields.qtyPerPack ? (
                <span>{commonFields.qtyPerPack}</span>
              ) : (
                <InputNumber
                  value={text}
                  onChange={(val) => updateRow(record.key, "qtyPerPack", val)}
                  style={{ width: "100%" }}
                  min={1}
                />
              ),
          },
        ]
      : [
          {
            title: "Quantity",
            dataIndex: "quantity",
            key: "quantity",
            width: 110,
            render: (text, record) =>
              commonFields.quantity ? (
                <span>{commonFields.quantity}</span>
              ) : (
                <InputNumber
                  value={text}
                  onChange={(val) => updateRow(record.key, "quantity", val)}
                  style={{ width: "100%" }}
                  min={1}
                />
              ),
          },
          {
            title: "Rate / Unit",
            dataIndex: "ratePerUnit",
            key: "ratePerUnit",
            width: 120,
            render: (text, record) =>
              commonFields.ratePerUnit ? (
                <span>{commonFields.ratePerUnit}</span>
              ) : (
                <InputNumber
                  value={text}
                  onChange={(val) => updateRow(record.key, "ratePerUnit", val)}
                  style={{ width: "100%" }}
                  min={0}
                />
              ),
          },
        ]),
    {
      title: "Ply",
      dataIndex: "ply",
      key: "ply",
      width: 90,
      render: (text, record) =>
        commonFields.ply ? (
          <span>{commonFields.ply}</span>
        ) : (
          <Select
            value={text}
            onChange={(val) => updateRow(record.key, "ply", val)}
            style={{ width: "100%" }}
            options={
              plyOptions?.map((p) => ({ label: p.name, value: p.value })) || []
            }
          />
        ),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Popconfirm
          title="Delete row?"
          onConfirm={() => deleteRow(record.key)}
        >
          <Button danger size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Drawer
      open={visible}
      onClose={onCancel}
      footer={null}
      width={980}
      placement="right"
      destroyOnClose
      mask={false}
      title={
        <Title level={4} style={{ margin: 0 }}>
          {currentStep === 1 ? "Bulk Add Items - Step 1" : "Bulk Add Items - Step 2"}
        </Title>
      }
    >
      {currentStep === 1 && (
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            layerSelector: "basic",
            print_selector: "no",
            isCoating: "no",
          }}
        >
          <Row gutter={24}>
            <Col xs={24} lg={10}>
              <Card bordered={false} title="Sizing & Configuration">
                <Form.Item
                  name="subcategory"
                  label="Subcategory"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Select
                    showSearch
                    placeholder="Select subcategory"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children?.toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={(val) =>
                      setCommonFields((prev) => ({ ...prev, subcategory: val }))
                    }
                  >
                    {subCategory.map((sub) => (
                      <Select.Option key={sub.id} value={sub.name}>
                        {sub.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                <Divider style={{ margin: "8px 0 16px" }}>
                  Optional Common Values
                </Divider>

                <Row gutter={16}>
                  <Col xs={24} sm={8}>
                    <Form.Item label="Length">
                      <InputNumber
                        style={{ width: "100%" }}
                        onChange={(val) =>
                          setCommonFields((prev) => ({ ...prev, length: val }))
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item label="Width">
                      <InputNumber
                        style={{ width: "100%" }}
                        onChange={(val) =>
                          setCommonFields((prev) => ({ ...prev, width: val }))
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item label="Height">
                      <InputNumber
                        style={{ width: "100%" }}
                        onChange={(val) =>
                          setCommonFields((prev) => ({ ...prev, height: val }))
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Unit">
                      <Select
                        allowClear
                        onChange={(val) =>
                          setCommonFields((prev) => ({ ...prev, unitType: val }))
                        }
                      >
                        {unitType?.map((u) => (
                          <Select.Option key={u} value={u}>
                            {u}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Casting">
                      <Select
                        allowClear
                        onChange={(val) =>
                          setCommonFields((prev) => ({ ...prev, castingType: val }))
                        }
                      >
                        {castingType?.map((c, i) => (
                          <Select.Option key={i} value={c.value}>
                            {c.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  {isCombined ? (
                    <>
                      <Col xs={24} sm={12}>
                        <Form.Item label="Packing Role">
                          <Select
                            allowClear
                            onChange={(val) =>
                              setCommonFields((prev) => ({ ...prev, packingRole: val }))
                            }
                          >
                            <Select.Option value="inner">Inner</Select.Option>
                            <Select.Option value="outer">Outer</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item label="Qty / Pack">
                          <InputNumber
                            style={{ width: "100%" }}
                            min={1}
                            onChange={(val) =>
                              setCommonFields((prev) => ({ ...prev, qtyPerPack: val }))
                            }
                          />
                        </Form.Item>
                      </Col>
                    </>
                  ) : (
                    <>
                      <Col xs={24} sm={12}>
                        <Form.Item label="Quantity">
                          <InputNumber
                            style={{ width: "100%" }}
                            onChange={(val) =>
                              setCommonFields((prev) => ({ ...prev, quantity: val }))
                            }
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item label="Rate / Unit">
                          <InputNumber
                            style={{ width: "100%" }}
                            onChange={(val) =>
                              setCommonFields((prev) => ({ ...prev, ratePerUnit: val }))
                            }
                          />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                </Row>

                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Top Conversion Rate /Kg">
                      <InputNumber
                        style={{ width: "100%" }}
                        onChange={(val) =>
                          setCommonFields((prev) => ({
                            ...prev,
                            topConversionRate: val,
                          }))
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Box Conversion Rate /Kg">
                      <InputNumber
                        style={{ width: "100%" }}
                        onChange={(val) =>
                          setCommonFields((prev) => ({
                            ...prev,
                            boxConversionRate: val,
                          }))
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item label="RS">
                      <InputNumber
                        style={{ width: "100%" }}
                        onChange={(val) =>
                          setCommonFields((prev) => ({ ...prev, rs: val }))
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item label="CS">
                      <InputNumber
                        style={{ width: "100%" }}
                        onChange={(val) =>
                          setCommonFields((prev) => ({ ...prev, cs: val }))
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Boxes per Sheet">
                      <InputNumber
                        style={{ width: "100%" }}
                        onChange={(val) =>
                          setCommonFields((prev) => ({
                            ...prev,
                            boxesPerSheet: val,
                          }))
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Sheets per Box">
                      <InputNumber
                        style={{ width: "100%" }}
                        onChange={(val) =>
                          setCommonFields((prev) => ({
                            ...prev,
                            sheetsPerBox: val,
                          }))
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Box Type">
                      <Select
                        allowClear
                        onChange={(val) =>
                          setCommonFields((prev) => ({ ...prev, boxType: val }))
                        }
                      >
                        <Select.Option value="inner">Inner</Select.Option>
                        <Select.Option value="outer">Outer</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Rate Type">
                      <Select
                        value={commonFields.rateType}
                        onChange={(val) =>
                          setCommonFields((prev) => ({ ...prev, rateType: val }))
                        }
                      >
                        <Select.Option value="individual">Individual</Select.Option>
                        <Select.Option value="innerwise">Innerwise</Select.Option>
                        <Select.Option value="outerwise">Outerwise</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                {commonFields.rateType && commonFields.rateType !== "individual" && (
                  <Form.Item label="No. of Inners">
                    <InputNumber
                      style={{ width: "100%" }}
                      onChange={(val) =>
                        setCommonFields((prev) => ({
                          ...prev,
                          packingQuantity: val,
                        }))
                      }
                    />
                  </Form.Item>
                )}

                <Form.Item label="Glueing">
                  <Radio.Group
                    onChange={(e) =>
                      setCommonFields((prev) => ({
                        ...prev,
                        joiningMethod: e.target.value,
                      }))
                    }
                  >
                    <Radio value="stitching">Stitching</Radio>
                    <Radio value="hand_pasting">Hand Pasting</Radio>
                  </Radio.Group>
                </Form.Item>

                <Form.Item label="Remarks">
                  <Input.TextArea
                    rows={2}
                    onChange={(e) =>
                      setCommonFields((prev) => ({
                        ...prev,
                        remarks: e.target.value,
                      }))
                    }
                  />
                </Form.Item>
              </Card>
            </Col>

            <Col xs={24} lg={14}>
              <Card bordered={false} title="Customization">
                <Form.Item label="Ply / Walls">
                  <Select
                    placeholder="Select Ply"
                    allowClear
                    onChange={handlePlyChange}
                  >
                    {plyOptions?.map((p) => (
                      <Select.Option key={p.value} value={p.value}>
                        {p.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                <LayerTemplateSelector
                  ply={layers.length}
                  layers={layers}
                  setLayers={setLayers}
                  gsm={gsm}
                  setGsm={setGsm}
                  bf={bf}
                  setBf={setBf}
                />

                <Form.Item label="Layer Selector" name="layerSelector">
                  <Radio.Group defaultValue="basic" onChange={handleSelectorChange}>
                    <Radio value="basic">Basic</Radio>
                    <Radio value="advance">Advance</Radio>
                  </Radio.Group>
                </Form.Item>

                {!layerSelector ? (
                  <LayerSelectorBasic
                    layers={layers}
                    setLayers={setLayers}
                    layerSelector={layerSelector}
                  />
                ) : (
                  <LayerSelectorAdvance
                    layers={layers}
                    gsm={gsm}
                    bf={bf}
                    setLayers={setLayers}
                    setGsm={setGsm}
                    setBf={setBf}
                    layerSelector={layerSelector}
                  />
                )}

                <Form.Item label="Printing" name="print_selector">
                  <Radio.Group defaultValue="no" onChange={handlePrintingChange}>
                    <Radio value="yes">Yes</Radio>
                    <Radio value="no">No</Radio>
                  </Radio.Group>
                </Form.Item>

                {isPrinting && (
                  <PrintSelector
                    printing={printing}
                    setPrinting={setPrinting}
                    isPrinting={isPrinting}
                  />
                )}

                <Form.Item label="Coating" name="isCoating">
                  <Radio.Group defaultValue="no" onChange={handleCoatingChange}>
                    <Radio value="yes">Yes</Radio>
                    <Radio value="no">No</Radio>
                  </Radio.Group>
                </Form.Item>

                {isCoating && (
                  <CoatingSelector
                    coating={coating}
                    setCoating={setCoating}
                    isCoating={isCoating}
                  />
                )}
              </Card>
            </Col>
          </Row>

          <Row justify="end">
            <Space>
              <Button icon={<ClearOutlined />} onClick={resetForm}>
                Reset
              </Button>
              <Button
                type="primary"
                icon={<ArrowRightOutlined />}
                onClick={handleProceedToStep2}
              >
                Proceed to Step 2
              </Button>
            </Space>
          </Row>
        </Form>
      )}

      {currentStep === 2 && (
        <>
          <Card bordered={false} style={{ marginBottom: 16 }}>
            <Space wrap>
              <Tag color="blue">{commonFields.subcategory}</Tag>
              {commonFields.ply ? <Tag>{commonFields.ply} Ply</Tag> : null}
              {isPrinting ? <Tag color="purple">Printing</Tag> : null}
              {isCoating ? <Tag color="cyan">Coating</Tag> : null}
            </Space>
          </Card>

          <Row justify="space-between" style={{ marginBottom: 16 }}>
            <Col>
              <Title level={5} style={{ margin: 0 }}>
                Add Items Below
              </Title>
            </Col>
            <Col>
              <Space>
                <Button onClick={() => setCurrentStep(1)}>Back</Button>
                <Button icon={<PlusOutlined />} type="dashed" onClick={addRow}>
                  Add Row
                </Button>
              </Space>
            </Col>
          </Row>

          {rows.length === 0 ? (
            <Empty description="No rows added yet" />
          ) : (
            <Table
              dataSource={rows}
              columns={tableColumns}
              rowKey="key"
              pagination={false}
              scroll={{ x: 1100 }}
            />
          )}

          <Row justify="end" style={{ marginTop: 16 }}>
            <Space>
              <Button icon={<ClearOutlined />} onClick={resetForm}>
                Reset
              </Button>
              <Button type="primary" onClick={handleBulkSave}>
                Add {rows.length} Items
              </Button>
            </Space>
          </Row>
        </>
      )}
    </Drawer>
  );
};

export default OrderBulkItemsModal;
